import { Link } from 'react-router-dom';
import { useWishlist } from '../../contexts/WishlistContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

// ─── Footer ──────────────────────────────────────────────────────────────────
export function Footer() {
  return (
    <footer style={{
      background: 'var(--ink)', color: 'rgba(245,240,232,0.4)',
      padding: '48px 28px', marginTop: 'auto'
    }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 32, marginBottom: 40 }}>
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", color: 'var(--gold)', fontSize: 22, marginBottom: 8 }}>
              Warbadobe Rentals
            </div>
            <p style={{ fontSize: 12, lineHeight: 1.8, color: 'rgba(245,240,232,0.4)' }}>
              Luxury clothing rental for college students & young adults. Rent, return, repeat.
            </p>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 16, color: 'rgba(245,240,232,0.3)' }}>Navigate</div>
            {[{ to: '/', label: 'Home' }, { to: '/browse', label: 'Browse' }, { to: '/orders', label: 'My Orders' }].map(l => (
              <Link key={l.to} to={l.to} style={{ display: 'block', fontSize: 12, color: 'rgba(245,240,232,0.5)', marginBottom: 10, textDecoration: 'none', letterSpacing: '0.06em', transition: 'color 0.2s' }}>{l.label}</Link>
            ))}
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 16, color: 'rgba(245,240,232,0.3)' }}>Policy</div>
            <p style={{ fontSize: 12, color: 'rgba(245,240,232,0.4)', lineHeight: 2 }}>
              ✓ No late fine policy<br />
              ✓ 1–2 day buffer included<br />
              ✓ Security deposit fully refundable<br />
              ✓ Professional dry-cleaning
            </p>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 16, color: 'rgba(245,240,232,0.3)' }}>Legal & Support</div>
            {[
              { to: '/terms',         label: 'Terms & Conditions' },
              { to: '/refund-policy', label: 'Refund Policy' },
              { to: '/support',       label: 'Support Centre' },
            ].map(l => (
              <Link key={l.to} to={l.to} style={{ display: 'block', fontSize: 12, color: 'rgba(245,240,232,0.5)', marginBottom: 10, textDecoration: 'none', letterSpacing: '0.06em' }}>{l.label}</Link>
            ))}
          </div>
        </div>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 24, textAlign: 'center', fontSize: 11, letterSpacing: '0.08em' }}>
          © 2025 Warbadobe Rentals · Built with luxury in mind
        </div>
      </div>
    </footer>
  );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
export function StatusBadge({ status }) {
  const colorMap = {
    'Pending': { bg: '#fff8e1', border: '#ffc107', color: '#856404' },
    'Approved': { bg: '#e8f5e9', border: '#4caf50', color: '#1b5e20' },
    'Shipped': { bg: '#e3f2fd', border: '#2196f3', color: '#0d47a1' },
    'Delivered': { bg: '#e8f5e9', border: '#388e3c', color: '#1b5e20' },
    'Active Rental': { bg: '#e0f7fa', border: '#00acc1', color: '#006064' },
    'Return Requested': { bg: '#fff3e0', border: '#ff9800', color: '#e65100' },
    'Returned': { bg: '#e3f2fd', border: '#1976d2', color: '#0d47a1' },
    'Completed': { bg: '#f3e5f5', border: '#9c27b0', color: '#4a148c' },
    'Rejected': { bg: '#ffebee', border: '#f44336', color: '#b71c1c' },
    'Cancelled': { bg: '#fafafa', border: '#9e9e9e', color: '#424242' },
  };
  const colors = colorMap[status] || { bg: '#f5f5f5', border: '#ccc', color: '#555' };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px',
      fontSize: '9px', fontWeight: 700, letterSpacing: '0.12em',
      textTransform: 'uppercase', borderRadius: 1,
      background: colors.bg, border: `1px solid ${colors.border}`, color: colors.color
    }}>
      {status}
    </span>
  );
}

// ─── Product Card ─────────────────────────────────────────────────────────────
export function ProductCard({ product }) {
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { user } = useAuth();
  const navigate = useNavigate();
  const inWishlist = isInWishlist(product._id);

  const primaryImage = product.images?.[0]?.url;
  const minPrice = Math.min(
    product.rentalPrices?.twoDays || 9999,
    product.rentalPrices?.fiveDays || 9999,
    product.rentalPrices?.oneWeek || 9999,
    product.rentalPrices?.twoWeeks || 9999
  );

  return (
    <div style={cardStyles.card} className="card-hover">
      {!product.isActive && (
        <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 2 }}>
          <span className="tag tag-red">Unavailable</span>
        </div>
      )}
      {/* Image */}
      <div
        style={cardStyles.imgWrap}
        onClick={() => navigate(`/product/${product._id}`)}
      >
        {primaryImage ? (
          <img src={primaryImage} alt={product.name} style={cardStyles.img} />
        ) : (
          <div style={cardStyles.imgPlaceholder}>👗</div>
        )}
      </div>

      {/* Info */}
      <div style={cardStyles.info} onClick={() => navigate(`/product/${product._id}`)}>
        <span className="tag">{product.category}</span>
        <h3 style={cardStyles.name}>{product.name}</h3>
        <div style={cardStyles.price}>From ₹{minPrice.toLocaleString()}</div>
        <div className="text-muted text-sm mt-4">Sizes: {product.sizes?.join(', ')}</div>
      </div>

      {/* Actions */}
      <div style={cardStyles.actions}>
        <button
          className="btn btn-primary btn-sm"
          style={{ flex: 1 }}
          onClick={() => navigate(`/product/${product._id}`)}
        >
          View Details
        </button>
        <button
          onClick={() => {
            if (!user) { navigate('/login'); return; }
            toggleWishlist(product._id);
          }}
          style={{
            ...cardStyles.wishlistBtn,
            borderColor: inWishlist ? '#c0392b' : 'var(--border)',
            color: inWishlist ? '#c0392b' : 'var(--muted)'
          }}
        >
          {inWishlist ? '♥' : '♡'}
        </button>
      </div>
    </div>
  );
}

const cardStyles = {
  card: {
    background: 'white', border: '1px solid var(--border)',
    overflow: 'hidden', position: 'relative', cursor: 'default'
  },
  imgWrap: { height: 260, overflow: 'hidden', cursor: 'pointer' },
  img: { width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s ease', display: 'block' },
  imgPlaceholder: {
    width: '100%', height: '100%', background: 'var(--gold-pale)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48
  },
  info: { padding: '16px 16px 8px', cursor: 'pointer' },
  name: { fontFamily: "'Cormorant Garamond', serif", fontSize: 18, margin: '8px 0 4px' },
  price: { fontSize: 13, color: 'var(--gold)', fontWeight: 600 },
  actions: { padding: '0 16px 16px', display: 'flex', gap: 8 },
  wishlistBtn: {
    background: 'transparent', border: '1px solid',
    padding: '7px 14px', fontSize: 18, cursor: 'pointer',
    transition: 'all 0.2s', lineHeight: 1, color: 'var(--muted)'
  }
};

// ─── Spinner ──────────────────────────────────────────────────────────────────
export function Spinner({ size = 'sm', centered = false }) {
  const s = size === 'lg' ? 48 : size === 'md' ? 32 : 20;
  const border = size === 'lg' ? 4 : 2;
  const el = (
    <div style={{
      width: s, height: s,
      border: `${border}px solid var(--border)`,
      borderTopColor: 'var(--gold)',
      borderRadius: '50%',
      animation: 'spin 0.7s linear infinite'
    }} />
  );
  if (centered) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
      {el}
    </div>
  );
  return el;
}

// ─── Empty State ──────────────────────────────────────────────────────────────
export function EmptyState({ icon = '📦', title, message, action, actionLabel }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">{icon}</div>
      <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontStyle: 'italic', marginBottom: 8 }}>{title}</h3>
      {message && <p className="text-muted" style={{ marginBottom: 24, fontSize: 13 }}>{message}</p>}
      {action && actionLabel && (
        <button className="btn btn-primary" onClick={action}>{actionLabel}</button>
      )}
    </div>
  );
}

// ─── Confirm Dialog ───────────────────────────────────────────────────────────
export function ConfirmDialog({ isOpen, title, message, onConfirm, onCancel, confirmLabel = 'Confirm', danger = false }) {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, marginBottom: 12 }}>{title}</h3>
        <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 28 }}>{message}</p>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
            style={{ flex: 1 }}
            onClick={onConfirm}
          >{confirmLabel}</button>
          <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Price Display ────────────────────────────────────────────────────────────
export function PriceRow({ label, amount, bold = false, gold = false }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
      <span style={{ fontSize: 13, fontWeight: bold ? 600 : 300, color: bold ? 'var(--ink)' : 'var(--muted)' }}>{label}</span>
      <span style={{ fontSize: bold ? 16 : 13, fontWeight: bold ? 700 : 400, color: gold ? 'var(--gold)' : 'var(--ink)' }}>
        ₹{Number(amount).toLocaleString()}
      </span>
    </div>
  );
}